/**
 * Data inlezen: uit een TopSolid-export of uit een bestaande houtstaat-werkmap.
 *
 * Het bestand wordt in de browser gelezen; de API krijgt de al ontlede onderdelen. Zo
 * hoeft er geen spreadsheetlezer in de Functions te zitten en ziet de voorbereider
 * meteen wat er in het bestand staat vóórdat er iets wordt opgeslagen.
 *
 * Deck en sectie staan niet in de export en worden hier gevraagd: zonder die twee is
 * niet te zeggen waar het werk in het schip zit. Ruimte en bouwelement komen uit het
 * bestand als ze erin staan.
 */
import { useState } from 'react';

import type { Onderdeel } from '@houtstaat/engine';

import { api } from '../api';
import { useGegevens } from '../gegevens';
import { kiesBlad, naarOnderdelen, VELDLABEL, type Doelplaatsing } from '../import/onderdelen';
import { leesXlsx } from '../import/xlsx';
import { Dialoog } from './Dialoog';

export type Importbron = 'TopSolid' | 'Excel';

/** Per bron het blad waar we eerst kijken. */
const VOORKEURSBLAD: Record<Importbron, string[]> = {
  TopSolid: ['Import'],
  Excel: ['Houtstaat ', 'Houtstaat', 'Import'],
};

interface Gelezen {
  bestandsnaam: string;
  blad: string;
  onderdelen: Onderdeel[];
  gevondenKolommen: string[];
  ontbrekend: string[];
  overgeslagen: Array<{ rij: number; reden: string }>;
}

export function Importdialoog({
  bron,
  doel,
  onSluiten,
  onKlaar,
}: {
  bron: Importbron;
  /** De plek waar de import landt; deck en sectie zijn hier aan te passen. */
  doel: Doelplaatsing;
  onSluiten: () => void;
  onKlaar: () => Promise<void>;
}) {
  const { meldFout } = useGegevens();
  const [plaats, setPlaats] = useState<Doelplaatsing>(doel);
  const [gelezen, setGelezen] = useState<Gelezen | null>(null);
  const [bezig, setBezig] = useState(false);

  const lees = async (bestand: File) => {
    setBezig(true);
    try {
      const bladen = leesXlsx(await bestand.arrayBuffer());
      const blad = kiesBlad(bladen, VOORKEURSBLAD[bron]);
      if (!blad) {
        meldFout(
          `In ${bestand.name} staat geen blad met onderdelen. Gevonden bladen: ${bladen
            .map((b) => b.naam)
            .join(', ')}.`,
        );
        return;
      }
      const resultaat = naarOnderdelen(blad, plaats);
      setGelezen({
        bestandsnaam: bestand.name,
        blad: resultaat.blad,
        onderdelen: resultaat.onderdelen,
        gevondenKolommen: resultaat.gevondenKolommen,
        ontbrekend: resultaat.ontbrekend.map((v) => VELDLABEL[v]),
        overgeslagen: resultaat.overgeslagen,
      });
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Het bestand kon niet gelezen worden.');
    } finally {
      setBezig(false);
    }
  };

  // Deck, sectie of karnummer wijzigen na het inlezen: opnieuw omzetten, want die
  // waarden zitten in elk onderdeel.
  const zetPlaats = (wijziging: Partial<Doelplaatsing>) => {
    const nieuw = { ...plaats, ...wijziging };
    setPlaats(nieuw);
    setGelezen((huidig) =>
      huidig
        ? {
            ...huidig,
            onderdelen: huidig.onderdelen.map((o) => ({
              ...o,
              plaatsing: {
                ...o.plaatsing,
                deck: nieuw.deck,
                sectie: nieuw.sectie,
                kar: nieuw.kar,
                karNaam: nieuw.karNaam,
              },
            })),
          }
        : huidig,
    );
  };

  const inlezen = async () => {
    if (!gelezen || gelezen.onderdelen.length === 0) return;
    setBezig(true);
    try {
      await api.importeren(plaats.bouwnummer, gelezen.onderdelen, `${bron} · ${gelezen.bestandsnaam}`);
      await onKlaar();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De import lukte niet.');
    } finally {
      setBezig(false);
    }
  };

  const perBouwelement = new Map<string, number>();
  for (const o of gelezen?.onderdelen ?? []) {
    const sleutel = `${o.plaatsing.ruimteNaam} · ${o.plaatsing.bouwelementNaam}`;
    perBouwelement.set(sleutel, (perBouwelement.get(sleutel) ?? 0) + 1);
  }

  return (
    <Dialoog
      titel={bron === 'TopSolid' ? 'Importeren uit TopSolid' : 'Excel inladen'}
      onSluiten={onSluiten}
      breedte={640}
      voet={
        <>
          <button type="button" className="hs-knop" onClick={onSluiten}>
            Annuleren
          </button>
          <button
            type="button"
            className="hs-knop hs-knop--primair"
            disabled={bezig || !gelezen || gelezen.onderdelen.length === 0}
            onClick={() => void inlezen()}
          >
            {gelezen ? `${gelezen.onderdelen.length} onderdelen inlezen` : 'Inlezen'}
          </button>
        </>
      }
    >
      <p className="hs-muted">
        {bron === 'TopSolid'
          ? 'Kies het exportbestand dat TopSolid heeft weggeschreven. De materiaalcodes gaan door de conversietabel; daarna bouwt de applicatie per kar de route.'
          : 'Kies een bestaande houtstaat-werkmap. De kolommen worden op naam herkend, dus de volgorde maakt niet uit.'}
      </p>

      <label className="hs-veld">
        <span>Bestand</span>
        <input
          type="file"
          accept=".xlsx,.xlsm"
          onChange={(e) => {
            const bestand = e.target.files?.[0];
            if (bestand) void lees(bestand);
            e.target.value = '';
          }}
        />
      </label>

      <div>
        <span className="hs-sectielabel">Waar landt dit werk?</span>
        <p className="hs-muted" style={{ margin: '3px 0 6px', fontSize: 11 }}>
          Deck en sectie staan niet in de export. Ruimte en bouwelement komen uit het bestand als ze
          erin staan.
        </p>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <label className="hs-veld" style={{ width: 70 }}>
            <span>Deck</span>
            <input
              className="hs-invoer hs-invoer--mono"
              value={plaats.deck}
              onChange={(e) => zetPlaats({ deck: e.target.value })}
            />
          </label>
          <label className="hs-veld" style={{ flex: 1, minWidth: 140 }}>
            <span>Karnaam</span>
            <input
              className="hs-invoer"
              value={plaats.karNaam}
              onChange={(e) => zetPlaats({ karNaam: e.target.value })}
            />
          </label>
          <label className="hs-veld" style={{ flex: 1, minWidth: 160 }}>
            <span>Sectie</span>
            <input
              className="hs-invoer"
              value={plaats.sectie}
              onChange={(e) => zetPlaats({ sectie: e.target.value })}
            />
          </label>
          <label className="hs-veld" style={{ width: 100 }}>
            <span>Karcode</span>
            <input
              className="hs-invoer hs-invoer--mono"
              value={plaats.kar}
              onChange={(e) => zetPlaats({ kar: e.target.value })}
            />
          </label>
        </div>
      </div>

      {gelezen ? (
        gelezen.ontbrekend.length > 0 ? (
          <div className="hs-melding">
            <span>
              In blad <b>{gelezen.blad}</b> zijn deze kolommen niet gevonden:{' '}
              <b>{gelezen.ontbrekend.join(', ')}</b>.
              <span style={{ display: 'block', marginTop: 5 }}>
                Wat er wél stond: {gelezen.gevondenKolommen.slice(0, 24).join(' · ') || '(geen kopregel)'}
              </span>
            </span>
          </div>
        ) : (
          <>
            <div className="hs-melding hs-melding--info">
              <span>
                <b>{gelezen.onderdelen.length} onderdelen</b> gelezen uit blad{' '}
                <b>{gelezen.blad}</b> van {gelezen.bestandsnaam}, verdeeld over{' '}
                {perBouwelement.size} {perBouwelement.size === 1 ? 'bouwelement' : 'bouwelementen'}.
                Elk bouwelement krijgt één kar; die is daarna te splitsen.
              </span>
            </div>

            <table className="hs-tabel">
              <thead>
                <tr>
                  <th>Ruimte · bouwelement</th>
                  <th className="hs-rechts">Onderdelen</th>
                </tr>
              </thead>
              <tbody>
                {[...perBouwelement.entries()].map(([naam, aantal]) => (
                  <tr key={naam}>
                    <td>{naam}</td>
                    <td className="hs-mono hs-rechts">{aantal}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {gelezen.overgeslagen.length > 0 ? (
              <div className="hs-melding hs-melding--hold">
                <span>
                  <b>
                    {gelezen.overgeslagen.length}{' '}
                    {gelezen.overgeslagen.length === 1 ? 'regel' : 'regels'} overgeslagen.
                  </b>
                  <span style={{ display: 'block', marginTop: 4 }}>
                    {gelezen.overgeslagen
                      .slice(0, 6)
                      .map((o) => `rij ${o.rij}: ${o.reden}`)
                      .join(' · ')}
                    {gelezen.overgeslagen.length > 6 ? ' · …' : ''}
                  </span>
                </span>
              </div>
            ) : null}

            <details>
              <summary className="hs-muted" style={{ cursor: 'pointer', fontSize: 11.5 }}>
                Eerste vijf onderdelen bekijken
              </summary>
              <table className="hs-tabel" style={{ marginTop: 5 }}>
                <thead>
                  <tr>
                    <th>Nr</th>
                    <th>Benaming</th>
                    <th className="hs-rechts">Aant</th>
                    <th className="hs-rechts">L × B × D</th>
                    <th>Materiaal</th>
                    <th>Zijde 0 / 5</th>
                  </tr>
                </thead>
                <tbody>
                  {gelezen.onderdelen.slice(0, 5).map((o) => (
                    <tr key={o.id}>
                      <td className="hs-mono">{o.nummer}</td>
                      <td>{o.benaming}</td>
                      <td className="hs-mono hs-rechts">{o.aantal}</td>
                      <td className="hs-mono hs-rechts">
                        {o.lengte} × {o.breedte} × {o.dikte}
                      </td>
                      <td className="hs-mono">{o.materiaal}</td>
                      <td className="hs-mono">
                        {o.zijden.zijde0 || '—'} / {o.zijden.zijde5 || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )
      ) : null}
    </Dialoog>
  );
}
